/**
 * CallRail Error Parser Module (Story 22.1, AC #5, AC #7)
 *
 * HTTP-status-first error handling (carry-forward from Ahrefs 20.1 / Semrush
 * 20.4 / Moz 20.7 / Reddit 19.10). Per
 * `support.callrail.com/hc/en-us/articles/12132320044173-HTTP-response-codes`
 * verified 2026-05-14, CallRail does NOT document structured error codes
 * beyond the HTTP status — the response body's shape is described only as
 * "extra information about what went wrong will be encoded in the response
 * as JSON". The parser defensively extracts a `message` field if present,
 * but tolerates absence.
 *
 *   - HTTP 400 / 422 → `validation_error` retryable=false (malformed
 *     request payload; common when the customer requests CI-gated fields
 *     without entitlement).
 *   - HTTP 401 → `token_expired` retryable=false (token is invalid or
 *     revoked; the customer rotated/revoked the token in the CallRail API
 *     keys dashboard).
 *   - HTTP 403 → `permission_denied` retryable=false (plan downgrade or
 *     user permission removal).
 *   - HTTP 404 → `account_not_found` (rare since helpers hardcode paths
 *     — usually means `account_id` not in the discovered allowlist).
 *   - HTTP 429 → `rate_limited` retryable=true. **No documented
 *     `Retry-After` or `X-RateLimit-*` headers** (verified docs 2026-05-14
 *     are silent on this) — the adapter falls back to a 60-second cooldown.
 *   - HTTP 5xx → `provider_unavailable` retryable=true with exponential
 *     backoff.
 *
 * **Sanitization (Round 3 Red Team Attack #3):** error messages MUST flow
 * through `sanitizeCallRailErrorMessage` before reaching any log line or
 * `AdapterError.message`. CallRail sends the API token as a custom
 * `Authorization: Token token="<KEY>"` header — the token can leak into
 * error message echoes (if CallRail echoes back what we sent in a
 * malformed-request error). The sanitizer redacts the literal token value,
 * URL-encoded variants, AND the `Token token="..."` header pattern.
 */

import type { AdapterErrorCode } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Types
// ============================================================================

/**
 * Closed enumeration of CallRail error types. Mirrors the Moz 20.7 + Semrush
 * 20.4 + Ahrefs 20.1 shape for cross-adapter consistency.
 */
export type CallRailErrorType =
  | "unauthorized" // 401 — invalid or revoked token
  | "forbidden" // 403 — plan downgrade / permission removal / subscription gate
  | "not_found" // 404 — endpoint hardcoded incorrectly OR account_id outside allowlist
  | "validation_error" // 400 / 422 — malformed request payload
  | "rate_limit_exceeded" // 429 — 1K/hr OR 10K/day quota breach
  | "provider_unavailable" // 5xx — CallRail regional outage
  | "unknown";

export interface CallRailApiError {
  readonly type: CallRailErrorType;
  readonly message: string;
  readonly status: number | null;
  readonly retryable: boolean;
  /** Optional `message` field extracted from the response body if present. */
  readonly providerMessage?: string;
}

// ============================================================================
// Status fallback map
// ============================================================================

const STATUS_TO_TYPE: Record<
  number,
  { type: CallRailErrorType; retryable: boolean }
> = {
  400: { type: "validation_error", retryable: false },
  401: { type: "unauthorized", retryable: false },
  403: { type: "forbidden", retryable: false },
  404: { type: "not_found", retryable: false },
  405: { type: "validation_error", retryable: false },
  406: { type: "validation_error", retryable: false },
  422: { type: "validation_error", retryable: false },
  429: { type: "rate_limit_exceeded", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

const TYPE_TO_ADAPTER_CODE: Record<CallRailErrorType, AdapterErrorCode> = {
  unauthorized: "token_expired",
  forbidden: "permission_denied",
  not_found: "account_not_found",
  validation_error: "invalid_request",
  rate_limit_exceeded: "rate_limited",
  provider_unavailable: "provider_unavailable",
  unknown: "invalid_response",
};

function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

// ============================================================================
// Sanitization (Round 3 Red Team Attack #3)
// ============================================================================

/**
 * Wraps the generic `sanitizeErrorMessage` helper with three CallRail-specific
 * defenses:
 *
 *  1. Replace every literal occurrence of the decrypted plaintext API token
 *     with `[redacted-callrail-token]`. Real CallRail tokens are 32-64
 *     alphanumeric chars per the API keys dashboard UI verified 2026-05-14;
 *     a 20-char threshold (matching the Zod schema minimum) prevents
 *     false-positives on legitimate prose substrings during dev-mode testing.
 *  2. Replace every `Token token="..."` header pattern with
 *     `Token token="[redacted-callrail-token]"` — defends against the case
 *     where the request headers leak into a fetch error message (e.g.
 *     "got 401 with Authorization: Token token=abc123def header").
 *  3. Replace the URL-encoded variant `Token+token%3D%22...%22` — defensive
 *     against debug endpoints or proxies that URL-encode header values in
 *     forensic logs.
 *
 * Multiline-aware so multiple headers / echoes on separate lines all get
 * caught. Bounded character classes prevent catastrophic backtracking on
 * adversarial inputs.
 */
export function sanitizeCallRailErrorMessage(
  message: string,
  decryptedToken?: string,
): string {
  // Run the generic sanitizer FIRST — it strips Bearer tokens, long base64
  // strings, generic OAuth-shape secrets.
  let sanitized = sanitizeErrorMessage(message);

  // (1) Replace `Token token="<value>"` header pattern. Bounded
  // `[^\s,;"'<>]{1,4096}` so adversarial inputs cannot trigger backtracking;
  // the token value runs until whitespace, comma, semicolon, or quote.
  // Case-insensitive in case CallRail echoes back lowercase variants.
  sanitized = sanitized.replace(
    /Token\s+token\s*=\s*"[^\s,;"<>]{1,4096}"/gi,
    'Token token="[redacted-callrail-token]"',
  );

  // (1b) Same pattern, single-quoted variant (some HTTP libraries
  // normalize to single quotes in error echoes).
  sanitized = sanitized.replace(
    /Token\s+token\s*=\s*'[^\s,;"<>]{1,4096}'/gi,
    "Token token='[redacted-callrail-token]'",
  );

  // (1c) Pass 1 review fix: unquoted variant — some proxy / intermediate
  // loggers strip header quotes when serializing for forensic logs.
  // Pattern: `Token token=abcDEF1234...` with no surrounding quotes.
  // Bounded to safe Token-charset (alphanumeric + dash + underscore)
  // so we don't accidentally strip whole sentences of prose.
  sanitized = sanitized.replace(
    /Token\s+token\s*=\s*([A-Za-z0-9_\-]{20,4096})/gi,
    "Token token=[redacted-callrail-token]",
  );

  // (2) URL-encoded variant: `Token+token%3D%22...%22` or
  // `Token%20token%3D%22...%22`. Defensive — CallRail uses headers, not
  // query params, but a future debug endpoint or proxy might URL-encode.
  sanitized = sanitized.replace(
    /Token[+%]20?token%3D%22[^\s,;"<>]{1,4096}%22/gi,
    'Token+token%3D%22[redacted-callrail-token]%22',
  );

  // (3) Exact-token replacement — defends against the case where the
  // decrypted plaintext token leaked into a non-header position (e.g. the
  // provider echoed the token value back in a malformed-request error body).
  // Threshold 20 chars matches the Zod connect-schema lower bound; below
  // 20 we skip the replacement to avoid false-positives on legitimate
  // prose substrings during dev-mode testing.
  if (decryptedToken && decryptedToken.length >= 20) {
    const escaped = decryptedToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(
      new RegExp(escaped, "g"),
      "[redacted-callrail-token]",
    );
    // URL-encoded variant of the raw token value.
    const urlEncoded = encodeURIComponent(decryptedToken);
    if (urlEncoded !== decryptedToken) {
      const escapedEncoded = urlEncoded.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      sanitized = sanitized.replace(
        new RegExp(escapedEncoded, "g"),
        "[redacted-callrail-token]",
      );
    }
  }

  return sanitized;
}

// ============================================================================
// HTTP-status-first parser (AC #7)
// ============================================================================

/**
 * Parse an HTTP error response from CallRail.
 *
 * CallRail does NOT document a structured error response body shape beyond
 * "extra information ... encoded as JSON". The parser defensively extracts
 * a `message` or `error` field from the JSON body if present, but falls
 * back gracefully when the body is non-JSON or lacks those fields.
 *
 * @param httpStatus - HTTP status code from the response.
 * @param body - Optional raw response body for forensic message context.
 * @param decryptedToken - Optional plaintext token for sanitization.
 */
export function parseCallRailApiError(
  httpStatus: number,
  body?: string,
  decryptedToken?: string,
): CallRailApiError {
  // Defensive JSON parse — if body is present and parseable, extract
  // common error-message field names. We do NOT throw on parse failure
  // (CallRail's docs are silent on the response body schema, so we
  // tolerate any shape).
  let providerMessage: string | undefined;
  if (body && body.trim().length > 0) {
    try {
      const parsed = JSON.parse(body) as Record<string, unknown>;
      const candidates = [
        parsed["message"],
        parsed["error"],
        parsed["errors"],
        parsed["detail"],
      ];
      for (const c of candidates) {
        if (typeof c === "string" && c.length > 0) {
          providerMessage = c;
          break;
        }
        // Some APIs return `errors: ["msg1", "msg2"]` — join those.
        if (Array.isArray(c) && c.length > 0 && typeof c[0] === "string") {
          providerMessage = c.join("; ");
          break;
        }
        // Pass 1 review fix: Rails-typical validation-error shape is
        // `errors: { field: ["msg1", "msg2"], other: ["msg3"] }`. The
        // previous code dropped this shape entirely. Flatten to
        // "field: msg1, msg2; other: msg3".
        if (c !== null && typeof c === "object" && !Array.isArray(c)) {
          const parts: string[] = [];
          for (const [field, val] of Object.entries(c as Record<string, unknown>)) {
            if (Array.isArray(val)) {
              const strings = val.filter((v): v is string => typeof v === "string");
              if (strings.length > 0) parts.push(`${field}: ${strings.join(", ")}`);
            } else if (typeof val === "string" && val.length > 0) {
              parts.push(`${field}: ${val}`);
            }
          }
          if (parts.length > 0) {
            providerMessage = parts.join("; ");
            break;
          }
        }
      }
    } catch {
      // Body isn't JSON — fall through to raw-body inclusion below.
    }
  }

  const mapping = STATUS_TO_TYPE[httpStatus];
  const baseMessage = `CallRail API error: HTTP ${httpStatus}${
    providerMessage ? ` — ${providerMessage}` : body ? ` — ${body.slice(0, 500)}` : ""
  }`;
  const sanitized = sanitizeCallRailErrorMessage(baseMessage, decryptedToken);

  if (mapping) {
    return {
      type: mapping.type,
      message: sanitized,
      status: httpStatus,
      retryable: mapping.retryable,
      providerMessage: providerMessage
        ? sanitizeCallRailErrorMessage(providerMessage, decryptedToken)
        : undefined,
    };
  }

  return {
    type: "unknown",
    message: sanitized,
    status: httpStatus,
    retryable: isRetryableStatus(httpStatus),
    providerMessage: providerMessage
      ? sanitizeCallRailErrorMessage(providerMessage, decryptedToken)
      : undefined,
  };
}

/**
 * Map a `CallRailErrorType` to the canonical `AdapterErrorCode` used by
 * the adapter contract.
 */
export function callRailErrorTypeToAdapterCode(
  type: CallRailErrorType,
): AdapterErrorCode {
  return TYPE_TO_ADAPTER_CODE[type];
}

// ============================================================================
// Throwable wrapper
// ============================================================================

/**
 * Throwable error class used by REST helpers to surface a parsed CallRail
 * error to the adapter orchestrator. Mirrors `SemrushApiCallError` /
 * `MozApiCallError` shape so the adapter dispatch logic stays uniform.
 */
export class CallRailApiCallError extends Error {
  readonly status: number;
  readonly type: CallRailErrorType;
  readonly retryable: boolean;
  readonly providerMessage?: string;

  constructor(parsed: CallRailApiError) {
    super(parsed.message);
    this.name = "CallRailApiCallError";
    this.status = parsed.status ?? 0;
    this.type = parsed.type;
    this.retryable = parsed.retryable;
    this.providerMessage = parsed.providerMessage;
  }
}
